import type {
  EvaluationScenario,
  ScenarioApplicability,
  ScenarioCheck,
  ScenarioCheckArgs,
  ScenarioControl,
  ScenarioRelation,
  ScenarioSource,
  ScenarioToolDouble,
} from '@/api/scenarioVersions';
import { stableJson } from '../scenarioMerge';

// ─────────────────────────────────────────────────────────────────────────────
// Edits the advanced editor makes to a version (screen 10; contract version 3 since 2026-11-02). Pure. The server re-derives the version's scope, carries
// the author's notes and decides what compiles; these only change what the person changed.
// ─────────────────────────────────────────────────────────────────────────────

/** A check type the editor offers, in words. The server re-derives a check's strength from its operator on save. */
export interface CheckType {
  operator: string;
  kind: string;
  strength: 'Exact' | 'Weak' | 'Manual';
  scope: 'Turn' | 'Conversation';
  label: string;
}

export const CHECK_TYPES: CheckType[] = [
  { operator: 'tool.called', kind: 'ToolEffect', strength: 'Exact', scope: 'Turn', label: 'Calls a tool' },
  { operator: 'peer.consulted', kind: 'ToolEffect', strength: 'Exact', scope: 'Turn', label: 'Asks a peer (whatever form of its name the agent uses)' },
  { operator: 'peer.notConsulted', kind: 'Prohibition', strength: 'Exact', scope: 'Turn', label: 'Does not ask a peer' },
  { operator: 'tool.notCalled', kind: 'Prohibition', strength: 'Exact', scope: 'Turn', label: 'Does not call a tool' },
  { operator: 'tool.notCalledWith', kind: 'Prohibition', strength: 'Exact', scope: 'Turn', label: 'Does not call a tool with certain arguments' },
  { operator: 'skills.loaded', kind: 'Grounding', strength: 'Exact', scope: 'Turn', label: 'Loads a skill' },
  { operator: 'skills.notLoaded', kind: 'Grounding', strength: 'Exact', scope: 'Turn', label: 'Does not load a skill' },
  { operator: 'claim.stated', kind: 'Consistency', strength: 'Exact', scope: 'Turn', label: 'States a figure in the right clause' },
  { operator: 'reply.severityScreenComplete', kind: 'InformationAcquisition', strength: 'Exact', scope: 'Turn', label: 'Asks the emergency screen' },
  { operator: 'reply.containsAll', kind: 'Communication', strength: 'Weak', scope: 'Turn', label: 'The reply mentions every phrase' },
  { operator: 'reply.containsAny', kind: 'Communication', strength: 'Weak', scope: 'Turn', label: 'The reply mentions one of the phrases' },
  { operator: 'reply.containsNone', kind: 'Prohibition', strength: 'Weak', scope: 'Turn', label: 'The reply mentions none of the phrases' },
  { operator: 'conversation.neverSays', kind: 'Prohibition', strength: 'Weak', scope: 'Conversation', label: 'No reply ever says a phrase' },
  { operator: 'conversation.saysAtLeastOnce', kind: 'Communication', strength: 'Weak', scope: 'Conversation', label: 'Some reply says one of the phrases' },
  { operator: 'conversation.numbersGrounded', kind: 'Grounding', strength: 'Exact', scope: 'Conversation', label: 'Numbers come from tool output' },
  { operator: 'manual.obligation', kind: 'Communication', strength: 'Manual', scope: 'Turn', label: 'A reviewer judges an obligation in words' },
];

const turnChecks = (s: EvaluationScenario, turn: number) => s.checks.filter((c) => c.scope === 'Turn' && c.turnIndex === turn);

/** A check id not yet used in the version: t{turn}.c{n} or r{rule}.c{n}. */
export function newCheckId(s: EvaluationScenario, prefix: string): string {
  const used = new Set(s.checks.map((c) => c.id));
  for (let n = 0; ; n++) if (!used.has(`${prefix}.c${n}`)) return `${prefix}.c${n}`;
}

/** Adds an empty check of a type at a turn or in a conversation rule (new rules get the next index and a name). */
export function addCheck(s: EvaluationScenario, type: CheckType, where: { turn?: number; ruleIndex?: number; ruleLabel?: string }): EvaluationScenario {
  const args: ScenarioCheckArgs =
    type.operator === 'reply.severityScreenComplete' ? { enabled: true }
      : type.operator === 'conversation.numbersGrounded' ? { enabled: true }
        : type.operator === 'reply.containsAny' ? { values: [], group: nextGroup(s, where.turn ?? 0) }
          : ['peer.consulted', 'peer.notConsulted', 'manual.obligation'].includes(type.operator) ? { value: '' }
            : {};
  if (type.scope === 'Turn') {
    const turn = where.turn ?? 0;
    return { ...s, checks: [...s.checks, { id: newCheckId(s, `t${turn}`), kind: type.kind, operator: type.operator, scope: 'Turn', turnIndex: turn, strength: type.strength, args }] };
  }
  const existing = s.checks.filter((c) => c.scope === 'Conversation').map((c) => c.ruleIndex ?? 0);
  const ruleIndex = where.ruleIndex ?? (existing.length ? Math.max(...existing) + 1 : 0);
  const label = where.ruleLabel ?? s.checks.find((c) => c.scope === 'Conversation' && c.ruleIndex === ruleIndex)?.label ?? 'New rule';
  return { ...s, checks: [...s.checks, { id: newCheckId(s, `r${ruleIndex}`), kind: type.kind, operator: type.operator, scope: 'Conversation', strength: type.strength, args, label, ruleIndex }] };
}

/** The runner reads up to four any-of groups per turn; a new one takes the next free group. */
function nextGroup(s: EvaluationScenario, turn: number): number {
  const used = new Set(turnChecks(s, turn).filter((c) => c.operator === 'reply.containsAny').map((c) => c.args.group ?? 1));
  for (let g = 1; g <= 4; g++) if (!used.has(g)) return g;
  return 5; // the server refuses it in words; the editor never hides the attempt
}

export function updateCheck(s: EvaluationScenario, id: string, change: Partial<ScenarioCheck>): EvaluationScenario {
  return { ...s, checks: s.checks.map((c) => (c.id === id ? { ...c, ...change } : c)) };
}

export function updateArgs(s: EvaluationScenario, id: string, change: Partial<ScenarioCheckArgs>): EvaluationScenario {
  return { ...s, checks: s.checks.map((c) => (c.id === id ? { ...c, args: { ...c.args, ...change } } : c)) };
}

export function removeCheck(s: EvaluationScenario, id: string): EvaluationScenario {
  return { ...s, checks: s.checks.filter((c) => c.id !== id) };
}

export function setApplicability(s: EvaluationScenario, id: string, a: ScenarioApplicability | undefined): EvaluationScenario {
  return updateCheck(s, id, { applicability: a });
}

export function setTurnText(s: EvaluationScenario, turn: number, text: string): EvaluationScenario {
  return { ...s, events: s.events.map((e) => (e.turnIndex === turn ? { ...e, text } : e)) };
}

export function addTurn(s: EvaluationScenario): EvaluationScenario {
  const turn = s.events.length ? Math.max(...s.events.map((e) => e.turnIndex)) + 1 : 0;
  return { ...s, events: [...s.events, { id: `t${turn}`, kind: 'ActorInput', turnIndex: turn, actor: 'User', text: '' }] };
}

/**
 * Removes a turn and what looks at it — its checks, its controls and the doubles bound to it; later turns move up one,
 * with theirs. A double that answers on every turn stays.
 */
export function removeTurn(s: EvaluationScenario, turn: number): EvaluationScenario {
  const events = s.events
    .filter((e) => e.turnIndex !== turn)
    .map((e) => (e.turnIndex > turn ? { ...e, turnIndex: e.turnIndex - 1, id: `t${e.turnIndex - 1}` } : e));
  const checks = s.checks
    .filter((c) => !(c.scope === 'Turn' && c.turnIndex === turn))
    .map((c) => (c.scope === 'Turn' && (c.turnIndex ?? 0) > turn ? { ...c, turnIndex: (c.turnIndex ?? 0) - 1 } : c));
  const controls = (s.controls ?? [])
    .filter((c) => c.turnIndex !== turn)
    .map((c) => (c.turnIndex > turn ? { ...c, turnIndex: c.turnIndex - 1 } : c));
  const toolDoubles = (s.toolDoubles ?? [])
    .filter((d) => d.turnIndex !== turn)
    .map((d) => (d.turnIndex != null && d.turnIndex > turn ? { ...d, turnIndex: d.turnIndex - 1 } : d));
  return { ...s, events, checks, controls: orNone(controls), toolDoubles: orNone(toolDoubles) };
}

/** The checks a turn holds, for the confirmation before removing it. */
export const checksAtTurn = turnChecks;

/** What else a turn holds that removing it removes: its controls and the doubles bound to it. */
export function heldAtTurn(s: EvaluationScenario, turn: number): { controls: ScenarioControl[]; doubles: ScenarioToolDouble[] } {
  return { controls: (s.controls ?? []).filter((c) => c.turnIndex === turn), doubles: (s.toolDoubles ?? []).filter((d) => d.turnIndex === turn) };
}

// ── Contract version 3: hazard, sources, tool doubles, siblings and controls ──

/** An empty list is sent as no list: the canonical form omits it, so a version that never had one keeps its hash. */
const orNone = <T,>(list: T[]): T[] | undefined => (list.length ? list : undefined);

export function setHazard(s: EvaluationScenario, hazard: string): EvaluationScenario {
  return { ...s, hazard: hazard ? hazard : undefined };
}

interface ListItems {
  sources: ScenarioSource;
  toolDoubles: ScenarioToolDouble;
  relations: ScenarioRelation;
  controls: ScenarioControl;
}

export function addItem<K extends keyof ListItems>(s: EvaluationScenario, key: K, item: ListItems[K]): EvaluationScenario {
  return { ...s, [key]: [...((s[key] as ListItems[K][] | undefined) ?? []), item] };
}

export function updateItem<K extends keyof ListItems>(s: EvaluationScenario, key: K, index: number, change: Partial<ListItems[K]>): EvaluationScenario {
  const list = (s[key] as ListItems[K][] | undefined) ?? [];
  return { ...s, [key]: list.map((x, i) => (i === index ? { ...x, ...change } : x)) };
}

export function removeItem<K extends keyof ListItems>(s: EvaluationScenario, key: K, index: number): EvaluationScenario {
  const list = (s[key] as ListItems[K][] | undefined) ?? [];
  return { ...s, [key]: orNone(list.filter((_, i) => i !== index)) };
}

/** A control id not yet used in the version: c0, c1… */
export function newControlId(s: EvaluationScenario): string {
  const used = new Set((s.controls ?? []).map((c) => c.id));
  for (let n = 0; ; n++) if (!used.has(`c${n}`)) return `c${n}`;
}

/** Equal by content, whatever the key order (for "unsaved changes"). */
export function sameVersion(a: EvaluationScenario | null, b: EvaluationScenario | null): boolean {
  return stableJson(a) === stableJson(b);
}
